// src/utils/exportar.js
// Utilidades de exportación a PDF y Excel para reportes contables
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

/**
 * Exportar tabla a Excel (.xlsx)
 * @param {string} nombre - Nombre del archivo
 * @param {string} hoja - Nombre de la hoja
 * @param {Array<string>} columnas - Headers
 * @param {Array<Array>} filas - Datos
 * @param {Array<Object>} [resumen] - Filas de resumen al final [{label, valor}]
 */
export function exportarExcel(nombre, hoja, columnas, filas, resumen = []) {
  const data = [columnas, ...filas];
  if (resumen.length > 0) {
    data.push([]); // fila vacía
    resumen.forEach(r => {
      const row = new Array(columnas.length).fill("");
      row[0] = r.label;
      row[columnas.length - 1] = r.valor;
      data.push(row);
    });
  }
  const ws = XLSX.utils.aoa_to_sheet(data);
  // Ancho de columnas automático
  ws["!cols"] = columnas.map((_, i) => ({
    wch: Math.max(columnas[i].length, ...filas.map(f => String(f[i] || "").length)) + 2
  }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, hoja);
  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  saveAs(new Blob([buf], { type: "application/octet-stream" }), `${nombre}.xlsx`);
}

/**
 * Exportar tabla a PDF
 * @param {string} nombre - Nombre del archivo
 * @param {string} titulo - Título del reporte
 * @param {Array<string>} columnas - Headers
 * @param {Array<Array>} filas - Datos
 * @param {Object} [opciones] - {orientacion, resumen, subtitulo, empresa}
 */
export function exportarPDF(nombre, titulo, columnas, filas, opciones = {}) {
  const { orientacion = "portrait", resumen = [], subtitulo = "", empresa = "" } = opciones;
  const doc = new jsPDF({ orientation: orientacion, unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();

  // Header
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text(titulo, pageW / 2, 15, { align: "center" });

  if (empresa) {
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(empresa, pageW / 2, 22, { align: "center" });
  }

  if (subtitulo) {
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(subtitulo, pageW / 2, empresa ? 28 : 22, { align: "center" });
  }

  const startY = empresa && subtitulo ? 33 : empresa || subtitulo ? 27 : 20;

  // Tabla
  autoTable(doc, {
    head: [columnas],
    body: filas,
    startY,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [25, 118, 210], textColor: 255, fontStyle: "bold", fontSize: 8 },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    margin: { left: 10, right: 10 },
  });

  // Resumen al final
  if (resumen.length > 0) {
    let y = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(9);
    resumen.forEach(r => {
      doc.setFont("helvetica", "bold");
      doc.text(r.label, 12, y);
      doc.setFont("helvetica", "normal");
      doc.text(String(r.valor), pageW - 12, y, { align: "right" });
      y += 5;
    });
  }

  // Pie de página
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.text(`Generado: ${new Date().toLocaleString("es-EC")}`, 10, doc.internal.pageSize.getHeight() - 8);
    doc.text(`Página ${i} de ${totalPages}`, pageW - 10, doc.internal.pageSize.getHeight() - 8, { align: "right" });
  }

  doc.save(`${nombre}.pdf`);
}
